import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import type { CrmTeamDetailResponse } from "../../../types/crm";
import { getCrmTeamById, deleteCrmTeam, updateCrmTeam } from "../../../services/crmTeams";
import { EditCrmTeamModal } from "./EditCrmTeamModal";
import "./CrmTeamDetailView.css";

export const CrmTeamDetailView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    const state = location.state as { from?: string; fromLabel?: string } | null;
    navigate(state?.from ?? "/crm/teams");
  };

  const backLabel = () => {
    const fromLabel = (location.state as { fromLabel?: string } | null)?.fromLabel;
    return fromLabel ?? "Schools";
  };
  const [team, setTeam] = useState<CrmTeamDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [changingDefault, setChangingDefault] = useState(false);
  const [selectedDefaultId, setSelectedDefaultId] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);

  const handleDelete = async () => {
    if (!team) return;
    if (!window.confirm(`Delete ${team.name}? This cannot be undone.`)) return;
    try {
      await deleteCrmTeam(team.id);
      navigate("/crm/teams");
    } catch (err) {
      console.error("Failed to delete team:", err);
    }
  };

  useEffect(() => {
    if (!id) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const data = await getCrmTeamById(id);
        setTeam(data);
      } catch (err) {
        console.error("Failed to load team:", err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  const handleSetDefaultContact = async () => {
    if (!team || !selectedDefaultId) return;
    try {
      await updateCrmTeam(team.id, {
        name: team.name,
        division: team.division,
        state: team.state,
        notes: team.notes,
        defaultContactCoachId: selectedDefaultId,
      });
      const updated = await getCrmTeamById(team.id);
      setTeam(updated);
      setChangingDefault(false);
      setSelectedDefaultId("");
    } catch {
      // keep open on error
    }
  };

  if (loading) {
    return (
      <div className="crm-team-detail">
        <div className="crm-team-detail__loading">
          <div className="crm-team-detail__spinner" />
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="crm-team-detail">
        <div className="crm-team-detail__not-found">Team not found.</div>
      </div>
    );
  }

  return (
    <div className="crm-team-detail">
      <div className="crm-team-detail__breadcrumb">
        <button className="crm-team-detail__back" onClick={handleBack}>
          ← {backLabel()}
        </button>
      </div>

      <div className="crm-team-detail__header">
        <div>
          <h1 className="crm-team-detail__name">{team.name}</h1>
          <div className="crm-team-detail__meta">
            {team.division && <span className="crm-team-detail__tag">{team.division}</span>}
            {team.state && <span className="crm-team-detail__tag">{team.state}</span>}
          </div>
        </div>
        <div className="crm-team-detail__actions">
          <button className="crm-team-detail__edit-btn" onClick={() => setShowEditModal(true)}>
            Edit
          </button>
          <button className="crm-team-detail__delete-btn" onClick={handleDelete}>
            Delete
          </button>
        </div>
      </div>

      <div className="crm-team-detail__details">
        <div className="crm-team-detail__details-grid">
          <DetailItem label="Division" value={team.division} />
          <DetailItem label="State" value={team.state} />
          <DetailItem label="City" value={team.city} />
          <DetailItem label="Conference" value={team.conference} />
          <DetailItem label="Region" value={team.region} />
          <DetailItem label="Team Twitter" value={team.teamTwitter ? `@${team.teamTwitter.replace(/^@/, '')}` : undefined} />
        </div>
        <div className="crm-team-detail__details-section">
          <span className="crm-team-detail__details-label">Notes</span>
          <p className={`crm-team-detail__details-notes${!team.notes ? " crm-team-detail__detail-value--empty" : ""}`}>
            {team.notes || "\u2014"}
          </p>
        </div>
      </div>

      <div className="crm-team-detail__section">
        <div className="crm-team-detail__section-header-row">
          <h2 className="crm-team-detail__section-title">
            Coaches
            {team.coaches.length > 0 && (
              <span className="crm-team-detail__count">{team.coaches.length}</span>
            )}
          </h2>
          {team.coaches.length > 0 && (
            <div className="crm-team-detail__default-contact">
              {team.defaultContactName ? (
                <span className="crm-team-detail__default-label">
                  Default contact: <strong>{team.defaultContactName}</strong>
                </span>
              ) : (
                <span className="crm-team-detail__default-label crm-team-detail__default-label--missing">
                  No default contact set
                </span>
              )}
              {changingDefault ? (
                <div className="crm-team-detail__default-change">
                  <select
                    className="crm-team-detail__default-select"
                    value={selectedDefaultId}
                    onChange={(e) => setSelectedDefaultId(e.target.value)}
                  >
                    <option value="">Select coach...</option>
                    {team.coaches.map((c) => (
                      <option key={c.id} value={c.id}>{c.displayName}</option>
                    ))}
                  </select>
                  <button className="crm-team-detail__default-save" onClick={handleSetDefaultContact} disabled={!selectedDefaultId}>
                    Save
                  </button>
                  <button className="crm-team-detail__default-cancel" onClick={() => { setChangingDefault(false); setSelectedDefaultId(""); }}>
                    Cancel
                  </button>
                </div>
              ) : (
                <button className="crm-team-detail__default-change-btn" onClick={() => { setChangingDefault(true); setSelectedDefaultId(team.defaultContactCoachId ?? ""); }}>
                  Change
                </button>
              )}
            </div>
          )}
        </div>

        {team.coaches.length === 0 ? (
          <div className="crm-team-detail__empty">No coaches linked to this school yet.</div>
        ) : (
          <div className="crm-team-detail__coaches">
            {team.coaches.map((coach) => (
              <div key={coach.id} className={`crm-team-detail__coach-card${coach.id === team.defaultContactCoachId ? " crm-team-detail__coach-card--default" : ""}`}>
                <div className="crm-team-detail__coach-name">
                  {coach.displayName}
                  {coach.id === team.defaultContactCoachId && (
                    <span className="crm-team-detail__default-badge">Default</span>
                  )}
                </div>
                {coach.roleTitle && (
                  <div className="crm-team-detail__coach-role">{coach.roleTitle}</div>
                )}
                <div className="crm-team-detail__coach-contact">
                  {coach.email && <span>{coach.email}</span>}
                  {coach.phone && <span>{coach.phone}</span>}
                  {coach.xHandle && <span>{coach.xHandle}</span>}
                </div>
                {coach.preferredContactMethod && (
                  <span className={`crm-team-detail__badge crm-team-detail__badge--${coach.preferredContactMethod.toLowerCase()}`}>
                    {coach.preferredContactMethod === "Quo" ? "Phone" : coach.preferredContactMethod}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {showEditModal && team && (
        <EditCrmTeamModal
          isOpen={showEditModal}
          team={team}
          onClose={() => setShowEditModal(false)}
          onSuccess={(updated) => {
            setTeam(updated);
            setShowEditModal(false);
          }}
        />
      )}
    </div>
  );
};

function DetailItem({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="crm-team-detail__detail-item">
      <span className="crm-team-detail__detail-label">{label}</span>
      <span className={`crm-team-detail__detail-value${!value ? " crm-team-detail__detail-value--empty" : ""}`}>
        {value || "\u2014"}
      </span>
    </div>
  );
}
